/**
 * Classifies a thrown value as a TRANSIENT transport failure — one that says nothing about the
 * correctness of the request and is expected to succeed if simply re-issued.
 *
 * This exists because an unactionable transport error was reaching users as a dead-end screen:
 * `fetch failed: FetchRequestCanceledException: Fetch request has been canceled (at
 * Expo/NativeResponse.swift:125)` rendered verbatim under "Something went wrong". Nothing in that
 * sentence is a user's problem, and nothing they can do changes it — so the app must recover on its
 * own instead of showing it.
 *
 * Deliberately dependency-free (`package.json` has an empty `dependencies`). That emptiness is the
 * invariant that lets `@-/com.npmjs.tanstack__react-query` import it without any risk of a cycle
 * back through the app packages, and keeps it safe for the web/server bundles.
 *
 * Note the contrast with `isDatabaseClosingError`, which matches on `error.name` ONLY and whose doc
 * argues against message matching. That is the right call there: it classifies an error WE throw,
 * so we control its `name`. Here the errors originate in native code, undici, and Hermes — the iOS
 * cancellation arrives with its identity only in the message text, wrapped inside an outer
 * `fetch failed`. Message matching is unavoidable, so the tests pin the exact observed production
 * strings: if a future Expo/RN release changes them, the suite fails loudly rather than silently
 * reclassifying a transient failure as a permanent one.
 */

/**
 * Which kind of transport failure this is. Callers use it to pick a recovery strategy — a
 * cancellation should be re-issued immediately (nothing is wrong; the request was simply
 * abandoned), whereas an offline/timeout failure deserves a backoff.
 */
export type TransientNetworkErrorKind = "canceled" | "offline" | "timeout";

/**
 * A request that was ABANDONED rather than failed: the screen unmounted mid-flight, the app was
 * backgrounded, a reload swapped the JS bundle, or an `AbortSignal` fired. On iOS, expo's fetch
 * surfaces this as `FetchRequestCanceledException` from `NativeResponse.swift` rather than as the
 * web-standard `AbortError`, which is why both spellings are matched.
 */
const CANCELED_PATTERN =
  /FetchRequestCanceledException|Fetch request has been canceled|AbortError|The operation was aborted|The user aborted a request|\bcancell?ed\b/i;

/** No route to the host: offline, DNS failure, connection refused/reset, or a dead worker. */
const OFFLINE_PATTERN =
  /fetch failed|Failed to fetch|Network request failed|NetworkError|ConnectException|Failed to connect|HTTP request failed|ECONNRESET|ECONNREFUSED|ENOTFOUND|socket hang up/i;

/** The host is reachable but did not answer in time. */
const TIMEOUT_PATTERN =
  /timed out|TimeoutError|ETIMEDOUT|UND_ERR_(?:CONNECT_TIMEOUT|HEADERS_TIMEOUT)/i;

/**
 * How far to walk the `cause` chain. The observed production error is an outer `fetch failed`
 * wrapping the real `FetchRequestCanceledException`, so testing only the top-level error misses it
 * (exactly the gap in `isTransientPullError`, which this replaces). Bounded, with a visited set, so
 * a self-referential or cyclic `cause` can never spin.
 */
const MAX_CAUSE_DEPTH = 5;

function classifyText(text: string): TransientNetworkErrorKind | null {
  // Order matters: a cancellation's message often ALSO contains "fetch failed" (the outer wrapper),
  // and "canceled" is the more specific — and more actionable — diagnosis of the two.
  if (CANCELED_PATTERN.test(text)) {
    return "canceled";
  }
  if (TIMEOUT_PATTERN.test(text)) {
    return "timeout";
  }
  if (OFFLINE_PATTERN.test(text)) {
    return "offline";
  }
  return null;
}

/**
 * The transient-failure category of `error`, or `null` if it is not a transport failure at all.
 *
 * Returns `null` for anything that is not an `Error` — including a bare string carrying the same
 * text. A non-Error throw is already anomalous, and treating one as retryable would let a
 * stringly-typed programmer error masquerade as a network blip.
 */
export function describeTransientNetworkError(error: unknown): TransientNetworkErrorKind | null {
  const visited = new Set<unknown>();
  let current: unknown = error;
  let fallbackKind: TransientNetworkErrorKind | null = null;

  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth += 1) {
    if (!(current instanceof Error) || visited.has(current)) {
      break;
    }
    visited.add(current);

    const kind = classifyText(`${current.name}: ${current.message}`);
    // A cancellation anywhere in the chain wins outright, however deep. The outer error is
    // routinely a generic wrapper — `new Error("fetch failed", { cause: FetchRequestCanceled… })`
    // — so returning the FIRST match would report "offline" for what is really an abandoned
    // request, and cost it the fast, backoff-free retry that fixes it.
    if (kind === "canceled") {
      return kind;
    }
    fallbackKind ??= kind;
    current = current.cause;
  }

  return fallbackKind;
}

/**
 * Whether `error` is a transient transport failure that is worth retrying transparently.
 *
 * Callers must NOT use this to skip error REPORTING. An error that survives retries and reaches a
 * user is worth knowing about whether or not it was transient — suppressing the report is how a
 * permanently-broken backend becomes invisible. Use {@link describeTransientNetworkError} to tag
 * the report instead, so transient noise groups separately from genuine defects.
 */
export function isTransientNetworkError(error: unknown): boolean {
  return describeTransientNetworkError(error) !== null;
}
